import React, { useEffect, useState } from "react";
import {
  Building2, Users, IndianRupee, Wallet, Clock, CheckCircle, TrendingUp,
} from "lucide-react";
import SAStatCard from "./SAStatCard";
import RecentInstitutes from "./RecentInstitutes";
import InstituteStatusChart from "./InstituteStatusChart";
import PlatformRevenueChart from "./PlatformRevenueChart";
import RecentActivity from "./RecentActivity";
import { PageHeader } from "../../ui";
import apiClient from "../../../utils/apiClient";
import "./sa-dashboard.scss";

/* ── Placeholder data (until backend APIs are wired) ── */
const PLACEHOLDER = {
  stats: {
    totalInstitutes: 12,
    activeInstitutes: 9,
    totalStudents: 847,
    totalRevenue: 285000,
    trialInstitutes: 3,
  },
  commission: {
    totalCommission: 0,
    totalCollected: 0,
    pendingPayout: 0,
    settledPayout: 0,
  },
  statusDistribution: [
    { status: "active", count: 9 },
    { status: "trial", count: 3 },
    { status: "inactive", count: 0 },
    { status: "suspended", count: 0 },
  ],
  revenueData: [
    { month: "Sep", revenue: 18000 },
    { month: "Oct", revenue: 24000 },
    { month: "Nov", revenue: 31000 },
    { month: "Dec", revenue: 42000 },
    { month: "Jan", revenue: 55000 },
    { month: "Feb", revenue: 62000 },
    { month: "Mar", revenue: 53000 },
  ],
  recentInstitutes: [
    { _id: "1", name: "ABC Academy", email: "admin@abc.com", plan: "Pro", studentCount: 120, status: "active" },
    { _id: "2", name: "XYZ Institute", email: "admin@xyz.com", plan: "Starter", studentCount: 45, status: "trial" },
    { _id: "3", name: "TechLearn Hub", email: "info@tlh.com", plan: "Free", studentCount: 18, status: "active" },
    { _id: "4", name: "Code Campus", email: "admin@cc.com", plan: "Pro", studentCount: 200, status: "active" },
    { _id: "5", name: "SkillBridge", email: "admin@sb.com", plan: "Starter", studentCount: 32, status: "trial" },
  ],
  recentActivities: [
    { _id: "a1", action: "New institute registered: SkillBridge", adminName: "System", timeAgo: "2h ago" },
    { _id: "a2", action: "Plan upgraded to Pro: ABC Academy", adminName: "Super Admin", timeAgo: "5h ago" },
    { _id: "a3", action: "Institute suspended: Demo School", adminName: "Super Admin", timeAgo: "1d ago" },
    { _id: "a4", action: "Bulk students imported: TechLearn Hub (45 students)", adminName: "System", timeAgo: "2d ago" },
    { _id: "a5", action: "Razorpay enabled: Code Campus", adminName: "Super Admin", timeAgo: "3d ago" },
  ],
};

const SADashboardPage = () => {
  const [data, setData] = useState(PLACEHOLDER);
  const [loading, setLoading] = useState(false); // eslint-disable-line

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/sa/dashboard");
      setData(res.data);
    } catch {
      // Silently fall back to placeholder
    } finally {
      setLoading(false);
    }
  };

  const { stats, commission, statusDistribution, revenueData, recentInstitutes, recentActivities } = data;

  return (
    <div className="sa-dashboard">
      <PageHeader
        title="Platform Overview"
        description="Manage all tenants and monitor platform health"
        icon={Building2}
      />

      {/* KPI Stats */}
      <div className="sa-grid-4">
        <SAStatCard
          title="Total Institutes"
          value={stats.totalInstitutes}
          change={`${stats.trialInstitutes} in trial`}
          changeType="positive"
          icon={Building2}
          iconClass="blue"
        />
        <SAStatCard
          title="Active Institutes"
          value={stats.activeInstitutes}
          icon={Building2}
          iconClass="emerald"
        />
        <SAStatCard
          title="Total Students"
          value={stats.totalStudents?.toLocaleString("en-IN")}
          icon={Users}
          iconClass="violet"
        />
        <SAStatCard
          title="Platform Revenue"
          value={`₹${(stats.totalRevenue / 1000).toFixed(0)}K`}
          icon={IndianRupee}
          iconClass="amber"
        />
      </div>

      {/* Commission Analytics */}
      {(commission?.totalCommission > 0 || commission?.totalCollected > 0) && (
        <div className="sa-grid-4">
          <SAStatCard
            title="LMS Collections"
            value={`₹${((commission.totalCollected || 0) / 1000).toFixed(1)}K`}
            icon={Wallet}
            iconClass="blue"
          />
          <SAStatCard
            title="Commission Earned"
            value={`₹${((commission.totalCommission || 0) / 1000).toFixed(1)}K`}
            icon={TrendingUp}
            iconClass="emerald"
          />
          <SAStatCard
            title="Pending Payouts"
            value={`₹${((commission.pendingPayout || 0) / 1000).toFixed(1)}K`}
            icon={Clock}
            iconClass="amber"
          />
          <SAStatCard
            title="Settled Payouts"
            value={`₹${((commission.settledPayout || 0) / 1000).toFixed(1)}K`}
            icon={CheckCircle}
            iconClass="violet"
          />
        </div>
      )}

      {/* Charts Row */}
      <div className="sa-grid-2-1">
        <PlatformRevenueChart data={revenueData} />
        <InstituteStatusChart data={statusDistribution} />
      </div>

      {/* Bottom Row */}
      <div className="sa-grid-2-1">
        <RecentInstitutes institutes={recentInstitutes} />
        <RecentActivity activities={recentActivities} />
      </div>
    </div>
  );
};

export default SADashboardPage;
